import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { PlusLg, PlayFill, PauseFill, SkipEndFill, XCircle, PencilSquare, CheckLg, XLg } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import AdminLayout, { isStaff } from '../AdminLayout';
import { LoadingBlock, ErrorBlock } from '../../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../../common/ui/notify';
import { formatPrice, carTitle, formatDateTime, localInputToUtc, utcToLocalInput } from '../../../common/format';
import { getSocket, emitAck } from '../../../common/auctionSocket';
import DateTimePicker from '../../../common/DateTimePicker';
import CarPicker from './CarPicker';
import './AdminAuction.css';

const L = { geo: 0, en: 1, ru: 2, am: 3 };
const t = (geo, en, ru, am) => [geo, en, ru, am][L[currentLanguage()] ?? 0];
const LOT_TONE = { pending: 'cx-badge-neutral', open: 'cx-badge-new', live: 'cx-badge-sale', closed: 'cx-badge-neutral', sold: 'cx-badge-sold', withdrawn: 'cx-badge-danger' };
// Statuses an operator may set by hand (the live lot is engine-controlled).
const LOT_MANUAL_STATUS = ['pending', 'open', 'withdrawn'];

function remainingText(endsAt, now) {
    if (!endsAt) return '—';
    const ms = new Date(endsAt).getTime() - now;
    if (ms <= 0) return '00:00';
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const AdminAuctionConsole = () => {
    const { id } = useParams();
    const eventId = parseInt(id, 10);
    const user = useSelector((s) => s.user);

    const [event, setEvent] = useState(null);
    const [lots, setLots] = useState([]);
    const [outcomes, setOutcomes] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [state, setState] = useState('loading');
    const [busy, setBusy] = useState(false);
    const [live, setLive] = useState({});       // lot_id -> socket state
    const [now, setNow] = useState(Date.now());
    const [lotForm, setLotForm] = useState({ car_id: '', start_price: '', reserve_price: '', buy_now_price: '', status: 'pending' });
    const [availableCars, setAvailableCars] = useState([]);
    const [bidWindow, setBidWindow] = useState('');
    const [startsInput, setStartsInput] = useState('');
    const [titleInput, setTitleInput] = useState('');
    const [nbgRate, setNbgRate] = useState(null);
    const [editingLotId, setEditingLotId] = useState(null);
    const [editPrices, setEditPrices] = useState({ start_price: '', reserve_price: '', buy_now_price: '' });
    const liveRef = useRef(live);
    liveRef.current = live;
    // Last server values we synced into the editors. A live event re-loads
    // frequently (socket), so only re-seed the form fields when the value on
    // the server actually changes — otherwise a reload would wipe the edit the
    // operator is in the middle of making.
    const syncedBidWindow = useRef(null);
    const syncedStarts = useRef(null);
    const syncedTitle = useRef(null);

    useEffect(() => {
        const srv = event && event.lot_duration_seconds != null ? String(event.lot_duration_seconds) : null;
        if (srv !== null && srv !== syncedBidWindow.current) { syncedBidWindow.current = srv; setBidWindow(srv); }
    }, [event]);
    useEffect(() => {
        const srv = event ? (event.title || '') : null;
        if (srv !== null && srv !== syncedTitle.current) { syncedTitle.current = srv; setTitleInput(srv); }
    }, [event]);
    useEffect(() => {
        const srv = event && event.starts_at ? utcToLocalInput(event.starts_at) : null;
        if (srv !== null && srv !== syncedStarts.current) { syncedStarts.current = srv; setStartsInput(srv); }
    }, [event]);

    const saveTitle = async () => {
        if (!titleInput.trim()) { notify.warning(t('მიუთითეთ დასახელება', 'Enter a title', 'Укажите название', 'Նշեք վերնագիրը')); return; }
        setBusy(true);
        try {
            await axios.put(`/api/auction/admin/events/${eventId}`, { title: titleInput.trim() });
            notify.success(t('დასახელება განახლდა', 'Title updated', 'Название обновлено', 'Վերնագիրը թարմացվեց'));
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const saveStarts = async () => {
        if (!startsInput) { notify.warning(t('მიუთითეთ დაწყების დრო', 'Set a start time', 'Укажите время', 'Նշեք ժամը')); return; }
        setBusy(true);
        try {
            await axios.put(`/api/auction/admin/events/${eventId}`, { starts_at: localInputToUtc(startsInput) });
            notify.success(t('დაწყების დრო განახლდა', 'Start time updated', 'Время начала обновлено', 'Մեկնարկի ժամը թարմացվեց'));
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    // Re-arm a live/paused/finished event: reset every lot and move the event
    // back to "scheduled" so the auto-scheduler starts it at its set start time.
    const reschedule = async () => {
        if (busy) return;
        const startsPast = event && event.starts_at && new Date(event.starts_at).getTime() <= Date.now();
        const warn = [
            t('ყველა ლოტი განულდება და ბიდები/გამარჯვებულები წაიშლება.',
              'Every lot will be reset and all bids/winners cleared.',
              'Все лоты будут сброшены, ставки и победители удалены.',
              'Բոլոր լոտերը կզրոյացվեն, հայտերն ու հաղթողները կջնջվեն:'),
            startsPast
                ? t('დაწყების დრო წარსულშია — აუქციონი მაშინვე დაიწყება. ჯერ დააყენეთ მომავალი დრო, თუ ავტომატურ დაწყებას ელოდებით.',
                    'The start time is in the past — the auction will start immediately. Set a future time first if you want it to auto-start later.',
                    'Время начала в прошлом — аукцион запустится сразу. Сначала задайте будущее время.',
                    'Մեկնարկի ժամն անցյալում է — աճուրդը կսկսվի անմիջապես: Նախ նշեք ապագա ժամ:')
                : t('აუქციონი ავტომატურად დაიწყება მითითებულ დროზე.',
                    'The auction will auto-start at the set time.',
                    'Аукцион запустится автоматически в заданное время.',
                    'Աճուրդը ավտոմատ կմեկնարկի նշված ժամին:'),
        ].join('\n\n');
        if (!window.confirm(warn)) return;
        setBusy(true);
        try {
            await axios.post(`/api/auction/admin/events/${eventId}/reschedule`, { reset_lots: true });
            notify.success(t('ივენთი დაგეგმილზე დაბრუნდა — დაიწყება მითითებულ დროზე', 'Re-armed — starts at the set time', 'Возвращено — запустится в заданное время', 'Վերադարձվեց — կմեկնարկի նշված ժամին'));
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };
    useEffect(() => { axios.get('/api/auction/admin/nbg-rate').then((r) => setNbgRate(r.data.usd_rate)).catch(() => {}); }, []);

    const saveBidWindow = async () => {
        setBusy(true);
        try {
            await axios.put(`/api/auction/admin/events/${eventId}`, { lot_duration_seconds: Number(bidWindow) });
            notify.success(t('ბიდის ფანჯარა განახლდა', 'Bid window updated', 'Окно ставки обновлено', 'Պատուհանը թարմացվեց'));
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const loadAll = useCallback(async () => {
        try {
            const [ev, oc, inv, avail] = await Promise.all([
                axios.get(`/api/auction/admin/events/${eventId}`),
                axios.get(`/api/auction/admin/outcomes?event_id=${eventId}`),
                axios.get(`/api/auction/admin/invoices`),
                axios.get(`/api/auction/admin/available-cars`).catch(() => ({ data: { cars: [] } })),
            ]);
            setEvent(ev.data.event);
            setLots(ev.data.lots || []);
            setOutcomes(oc.data.outcomes || []);
            setAvailableCars(avail.data.cars || []);
            const lotIds = new Set((ev.data.lots || []).map((l) => l.id));
            setInvoices((inv.data.invoices || []).filter((i) => lotIds.has(i.lot_id)));
            setState('ready');
            return ev.data.lots || [];
        } catch (e) {
            setState('error');
            return [];
        }
    }, [eventId]);

    // Initial load + join each lot room for live state.
    useEffect(() => {
        if (!isStaff(user)) return undefined;
        let alive = true;
        const socket = getSocket();
        const onState = (s) => { if (s && s.lot_id) setLive((p) => ({ ...p, [s.lot_id]: s })); };
        const onClosed = (m) => { if (m && m.result && m.result.lot_id) { loadAll(); } };
        socket.on('lot:state', onState);
        socket.on('lot:closed', onClosed);

        loadAll().then((loaded) => {
            if (!alive) return;
            loaded.forEach((lot) => emitAck('lot:join', { lot_id: lot.id }).then((ack) => {
                if (ack && ack.state) setLive((p) => ({ ...p, [lot.id]: ack.state }));
            }));
        });

        return () => { alive = false; socket.off('lot:state', onState); socket.off('lot:closed', onClosed); };
    }, [loadAll, user]);

    useEffect(() => { const x = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(x); }, []);

    const runOp = async (ev, payload, okMsg) => {
        setBusy(true);
        try {
            const ack = await emitAck(ev, payload);
            if (ack && ack.ok) { notify.success(okMsg); await loadAll(); }
            else notify.error({ en: `Failed: ${ack && ack.reason}`, geo: `ვერ შესრულდა: ${ack && ack.reason}` });
        } finally { setBusy(false); }
    };

    const addLot = async (e) => {
        e.preventDefault();
        if (!lotForm.car_id) { notify.warning(t('ჯერ აირჩიეთ ავტომობილი', 'Choose a car first', 'Сначала выберите авто', 'Նախ ընտրեք մեքենա')); return; }
        setBusy(true);
        try {
            await axios.post(`/api/auction/admin/events/${eventId}/lots`, {
                car_id: Number(lotForm.car_id), start_price: Number(lotForm.start_price),
                reserve_price: lotForm.reserve_price || undefined, buy_now_price: lotForm.buy_now_price || undefined,
                status: lotForm.status,
            });
            notify.success(t('ლოტი დაემატა', 'Lot added', 'Лот добавлен', 'Լոտն ավելացվեց'));
            setLotForm({ car_id: '', start_price: '', reserve_price: '', buy_now_price: '', status: 'pending' });
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const changeLotStatus = async (lotId, status) => {
        if (!status || busy) return;
        setBusy(true);
        try {
            await axios.patch(`/api/auction/admin/lots/${lotId}/status`, { status });
            notify.success(t('ლოტის სტატუსი განახლდა', 'Lot status updated', 'Статус лота обновлён', 'Լոտի կարգավիճակը թարմացվեց'));
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    // Rate used to preview GEL and to pre-fill USD from stored GEL amounts.
    const priceRate = (event && event.usd_rate) ? Number(event.usd_rate) : nbgRate;
    const usdFromGel = (gel) => (gel != null && priceRate ? String(Math.round((Number(gel) / priceRate) * 100) / 100) : '');

    const startEditPrices = (lot) => {
        setEditingLotId(lot.id);
        setEditPrices({
            start_price: lot.start_price_usd != null ? String(lot.start_price_usd) : usdFromGel(lot.start_price),
            reserve_price: lot.reserve_price_usd != null ? String(lot.reserve_price_usd) : (lot.reserve_price != null ? usdFromGel(lot.reserve_price) : ''),
            buy_now_price: lot.buy_now_price != null ? usdFromGel(lot.buy_now_price) : '',
        });
    };
    const cancelEditPrices = () => { setEditingLotId(null); };
    const saveEditPrices = async (lotId) => {
        if (busy) return;
        if (editPrices.start_price === '' || !(Number(editPrices.start_price) >= 0)) {
            notify.warning(t('მიუთითეთ საწყისი ბიდი ($)', 'Enter the start bid ($)', 'Укажите старт. цену ($)', 'Նշեք մեկնարկային գինը ($)'));
            return;
        }
        setBusy(true);
        try {
            await axios.patch(`/api/auction/admin/lots/${lotId}/prices`, {
                start_price: editPrices.start_price,
                reserve_price: editPrices.reserve_price === '' ? '' : editPrices.reserve_price,
                buy_now_price: editPrices.buy_now_price === '' ? '' : editPrices.buy_now_price,
            });
            notify.success(t('ლოტის ფასები განახლდა', 'Lot prices updated', 'Цены обновлены', 'Գները թարմացվեցին'));
            setEditingLotId(null);
            await loadAll();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const decide = async (lotId, decision) => {
        let counter_price;
        if (decision === 'counter') {
            const v = window.prompt(t('საპასუხო ფასი (GEL)', 'Counter price (GEL)', 'Встречная цена (GEL)', 'Հակագին (GEL)'));
            if (!v) return;
            counter_price = Number(v);
        }
        await runOp('op:decision', { lot_id: lotId, decision, counter_price }, t('გადაწყვეტილება მიღებულია', 'Decision applied', 'Решение применено', 'Որոշումը կիրառվեց'));
    };

    const invoiceAction = async (invoiceId, action) => {
        await runOp('op:invoice', { invoice_id: invoiceId, action }, t('განახლდა', 'Updated', 'Обновлено', 'Թարմացվեց'));
    };

    if (state === 'loading') return <AdminLayout title="..."><LoadingBlock label={t('იტვირთება...', 'Loading...', 'Загрузка...', 'Բեռնվում...')} /></AdminLayout>;
    if (state === 'error' || !event) return <AdminLayout title="..."><ErrorBlock title={t('შეცდომა', 'Error', 'Ошибка', 'Սխալ')} retryLabel={t('თავიდან', 'Retry', 'Повтор', 'Կրկին')} onRetry={loadAll} /></AdminLayout>;

    const pending = outcomes.filter((o) => o.decision === 'pending_seller');

    return (
        <AdminLayout title={event.title || `#${event.id}`}
            subtitle={formatDateTime(event.starts_at)}
            actions={
                <div className="admin-console-event-ops">
                    <span className={`cx-badge ${LOT_TONE[event.status] || 'cx-badge-neutral'}`}>{event.status}</span>
                    {(event.status === 'scheduled' || event.status === 'finished') && (
                        <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={() => runOp('op:start', { event_id: eventId }, t('აუქციონი დაიწყო', 'Auction started', 'Аукцион начался', 'Աճուրդը սկսվեց'))}>
                            <PlayFill /> {t('დაწყება', 'Start', 'Старт', 'Սկսել')}
                        </button>
                    )}
                    {event.status !== 'paused'
                        ? <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => runOp('op:pause', { event_id: eventId }, t('შეჩერდა', 'Paused', 'Пауза', 'Դադարեցվեց'))}><PauseFill /> {t('პაუზა', 'Pause', 'Пауза', 'Դադար')}</button>
                        : <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => runOp('op:resume', { event_id: eventId }, t('გაგრძელდა', 'Resumed', 'Продолжено', 'Վերսկսվեց'))}><PlayFill /> {t('გაგრძელება', 'Resume', 'Продолжить', 'Վերսկսել')}</button>}
                    <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={() => runOp('op:next', { event_id: eventId, duration_seconds: 60 }, t('შემდეგი ლოტი', 'Next lot', 'Следующий лот', 'Հաջորդ լոտ'))}><SkipEndFill /> {t('შემდეგი', 'Next', 'Далее', 'Հաջորդ')}</button>
                </div>
            }>
            <Link to="/admin/auction/events" className="admin-link admin-console-back">← {t('ყველა ივენთი', 'All events', 'Все события', 'Բոլոր իվենթները')}</Link>

            <div className="admin-console-bidwindow">
                <label>{t('დასახელება', 'Title', 'Название', 'Վերնագիր')}</label>
                <input className="cx-input" style={{ minWidth: 220 }} value={titleInput} onChange={(e) => setTitleInput(e.target.value)}
                    placeholder={t('ივენთის დასახელება', 'Event title', 'Название события', 'Իվենթի վերնագիր')} />
                <button className="cx-btn cx-btn-subtle cx-btn-sm" disabled={busy || !titleInput.trim() || titleInput.trim() === (event.title || '')} onClick={saveTitle}>
                    {t('შენახვა', 'Save', 'Сохранить', 'Պահպանել')}
                </button>
                <span className="admin-table-secondary">{t('მხოლოდ სახელია — დაწყების დროს არ ცვლის', 'Just a label — does not change the start time', 'Просто название — не меняет время начала', 'Պարզապես անուն է — ժամը չի փոխում')}</span>
            </div>

            <div className="admin-console-bidwindow">
                <label>{t('დაწყების თარიღი და დრო', 'Start date & time', 'Дата и время начала', 'Մեկնարկի ամսաթիվ')}</label>
                <DateTimePicker value={startsInput} onChange={setStartsInput} />
                <button className="cx-btn cx-btn-subtle cx-btn-sm" disabled={busy || !startsInput || startsInput === utcToLocalInput(event.starts_at)} onClick={saveStarts}>
                    {t('შენახვა', 'Save', 'Сохранить', 'Պահպանել')}
                </button>
                <span className="admin-table-secondary">
                    {event.status === 'scheduled'
                        ? t('მითითებულ დროზე ავტომატურად დაიწყება', 'Starts automatically at this time', 'Запустится автоматически в это время', 'Կմեկնարկի ավտոմատ այս ժամին')
                        : t('ადგილობრივი დრო', 'Local time', 'Местное время', 'Տեղական ժամ')}
                </span>
            </div>

            {(event.status === 'live' || event.status === 'paused' || event.status === 'finished') && (
                <div className="admin-console-bidwindow">
                    <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={reschedule}>
                        {t('ხელახლა დაგეგმვა (ლოტების განულება)', 'Re-arm (reset lots)', 'Перезапустить (сброс лотов)', 'Վերագործարկել (զրոյացում)')}
                    </button>
                    <span className="admin-table-secondary">
                        {event.status === 'finished'
                            ? t('ივენთი დასრულდა. იმავე ლოტებით ხელახლა გასაშვებად დააყენეთ ზემოთ ახალი დრო და დააჭირეთ „ხელახლა დაგეგმვას“ — დაგეგმილზე დაბრუნდება და მითითებულ დროზე ავტომატურად დაიწყება.',
                               'The event is finished. To run it again with the same lots, set a new time above and click “Re-arm” — it goes back to scheduled and auto-starts at that time.',
                               'Событие завершено. Чтобы запустить снова с теми же лотами, задайте новое время выше и нажмите «Перезапустить».',
                               'Իվենթն ավարտվել է: Նույն լոտերով նորից մեկնարկելու համար վերևում նշեք նոր ժամ և սեղմեք «Վերագործարկել»:')
                            : t('ივენთი აქტიურია. რომ მითითებულ დროზე ავტომატურად დაიწყოს, ჯერ დააყენეთ ზემოთ დრო, შემდეგ „ხელახლა დაგეგმვა“.',
                               'The event is active. To auto-start it at the set time, set the time above, then “Re-arm”.',
                               'Событие активно. Чтобы запустить автоматически в заданное время, задайте время выше, затем «Перезапустить».',
                               'Իվենթն ակտիվ է: Նշված ժամին ավտոմատ մեկնարկելու համար վերևում նշեք ժամը, ապա «Վերագործարկել»:')}
                    </span>
                </div>
            )}

            <div className="admin-console-bidwindow">
                <label>{t('ბიდის ფანჯარა (წმ)', 'Bid window (sec)', 'Окно ставки (сек)', 'Հայտի պատուհան (վրկ)')}</label>
                <input className="cx-input" type="number" min="5" max="3600" value={bidWindow} onChange={(e) => setBidWindow(e.target.value)} />
                <button className="cx-btn cx-btn-subtle cx-btn-sm" disabled={busy || !bidWindow || Number(bidWindow) === Number(event.lot_duration_seconds)} onClick={saveBidWindow}>
                    {t('შენახვა', 'Save', 'Сохранить', 'Պահպանել')}
                </button>
                <span className="admin-table-secondary">{t('ყოველი ბიდი ტაიმერს ამ მნიშვნელობაზე აბრუნებს', 'Every bid resets the timer to this', 'Каждая ставка сбрасывает таймер', 'Յուրաքանչյուր հայտ զրոյացնում է')}</span>
            </div>

            {/* Lots */}
            <div className="admin-table-wrapper">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{t('მანქანა', 'Car', 'Авто', 'Մեքենա')}</th>
                            <th>{t('ფასი', 'Price', 'Цена', 'Գին')}</th>
                            <th>{t('reserve', 'Reserve', 'Резерв', 'Ռեզերվ')}</th>
                            <th>{t('ტაიმერი', 'Timer', 'Таймер', 'Ժամաչափ')}</th>
                            <th>{t('სტატუსი', 'Status', 'Статус', 'Կարգավիճակ')}</th>
                            <th className="admin-table-actions-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lots.map((lot) => {
                            const ls = live[lot.id];
                            const price = ls ? ls.current_price : (lot.current_price || lot.start_price);
                            const status = (ls && ls.status) || lot.status;
                            return (
                                <Fragment key={lot.id}>
                                <tr>
                                    <td>{lot.position}</td>
                                    <td>
                                        <Link to={`/auction/lot/${lot.id}`} className="admin-link" target="_blank">{carTitle(lot)}</Link>
                                    </td>
                                    <td><strong>{formatPrice(price, 'GEL')}</strong></td>
                                    <td>{lot.reserve_price != null ? formatPrice(lot.reserve_price, 'GEL') : '—'}</td>
                                    <td className="admin-console-timer">{status === 'live' ? remainingText(ls && ls.ends_at, now) : '—'}</td>
                                    <td>
                                        {status === 'live' ? (
                                            <span className={`cx-badge ${LOT_TONE[status] || 'cx-badge-neutral'}`}>{status}</span>
                                        ) : (
                                            <select className="cx-select admin-lot-status" value={LOT_MANUAL_STATUS.includes(lot.status) ? lot.status : ''} disabled={busy}
                                                onChange={(e) => changeLotStatus(lot.id, e.target.value)} title={t('სტატუსის შეცვლა', 'Change status', 'Изменить статус', 'Փոխել կարգավիճակը')}>
                                                {!LOT_MANUAL_STATUS.includes(lot.status) && <option value="" disabled>{lot.status}</option>}
                                                <option value="pending">pending</option>
                                                <option value="open">open (pre-bid)</option>
                                                <option value="withdrawn">withdrawn</option>
                                            </select>
                                        )}
                                    </td>
                                    <td>
                                        <div className="admin-row-actions">
                                            {['pending', 'open', 'withdrawn'].includes(status) && (
                                                <button className={`admin-icon-button ${editingLotId === lot.id ? 'is-active' : ''}`} disabled={busy} title={t('ფასების რედაქტირება ($)', 'Edit prices ($)', 'Изменить цены ($)', 'Խմբագրել գները ($)')}
                                                    onClick={() => (editingLotId === lot.id ? cancelEditPrices() : startEditPrices(lot))}><PencilSquare /></button>
                                            )}
                                            {(status === 'pending' || status === 'open') && (
                                                <button className="admin-icon-button" disabled={busy} title={t('გაშვება', 'Go live', 'Запустить', 'Մեկնարկ')}
                                                    onClick={() => runOp('op:golive', { lot_id: lot.id, duration_seconds: 60 }, t('ლოტი გაეშვა', 'Lot is live', 'Лот запущен', 'Լոտը մեկնարկեց'))}><PlayFill /></button>
                                            )}
                                            {(status === 'open' || status === 'live') && (
                                                <button className="admin-icon-button is-danger" disabled={busy} title={t('მოხსნა', 'Withdraw', 'Снять', 'Հանել')}
                                                    onClick={() => runOp('op:withdraw', { lot_id: lot.id }, t('ლოტი მოიხსნა', 'Lot withdrawn', 'Лот снят', 'Լոտը հանվեց'))}><XCircle /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {editingLotId === lot.id && (
                                    <tr className="admin-lot-edit-row">
                                        <td colSpan={7}>
                                            <div className="admin-lot-edit">
                                                <label>{t('საწყისი ($)', 'Start ($)', 'Старт ($)', 'Մեկն. ($)')}
                                                    <input className="cx-input" type="number" step="0.01" min="0" value={editPrices.start_price}
                                                        onChange={(e) => setEditPrices((p) => ({ ...p, start_price: e.target.value }))} />
                                                </label>
                                                <label>{t('reserve ($)', 'Reserve ($)', 'Резерв ($)', 'Ռեզերվ ($)')}
                                                    <input className="cx-input" type="number" step="0.01" min="0" placeholder={t('ცარიელი = მოხსნა', 'empty = none', 'пусто = нет', 'դատարկ = չկա')} value={editPrices.reserve_price}
                                                        onChange={(e) => setEditPrices((p) => ({ ...p, reserve_price: e.target.value }))} />
                                                </label>
                                                <label>{t('buy-now ($)', 'Buy-now ($)', 'Купить ($)', 'Buy-now ($)')}
                                                    <input className="cx-input" type="number" step="0.01" min="0" placeholder={t('ცარიელი = მოხსნა', 'empty = none', 'пусто = нет', 'դատարկ = չկա')} value={editPrices.buy_now_price}
                                                        onChange={(e) => setEditPrices((p) => ({ ...p, buy_now_price: e.target.value }))} />
                                                </label>
                                                <div className="admin-lot-edit-actions">
                                                    <button className="cx-btn cx-btn-primary" disabled={busy} onClick={() => saveEditPrices(lot.id)}><CheckLg /> {t('შენახვა', 'Save', 'Сохранить', 'Պահպանել')}</button>
                                                    <button className="cx-btn cx-btn-ghost" disabled={busy} onClick={cancelEditPrices}><XLg /> {t('გაუქმება', 'Cancel', 'Отмена', 'Չեղարկել')}</button>
                                                </div>
                                                {priceRate && (
                                                    <span className="admin-table-secondary admin-lot-edit-hint">
                                                        1$ = ₾{priceRate}
                                                        {editPrices.start_price !== '' && Number.isFinite(Number(editPrices.start_price)) ? ` · ${t('საწყისი', 'start', 'старт', 'մեկն.')} ≈ ${formatPrice(Math.round(Number(editPrices.start_price) * priceRate * 100) / 100, 'GEL')}` : ''}
                                                        {editPrices.reserve_price !== '' && Number.isFinite(Number(editPrices.reserve_price)) ? ` · reserve ≈ ${formatPrice(Math.round(Number(editPrices.reserve_price) * priceRate * 100) / 100, 'GEL')}` : ''}
                                                        {' — '}{t('ბიდები ლარში', 'bids in GEL', 'ставки в GEL', 'հայտերը GEL-ով')}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </Fragment>
                            );
                        })}
                        {lots.length === 0 && <tr><td colSpan={7} className="admin-table-secondary">{t('ლოტები არ არის', 'No lots', 'Нет лотов', 'Լոտեր չկան')}</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Add lot */}
            <form className="cx-card admin-auction-form" onSubmit={addLot}>
                <h4>{t('ლოტის დამატება', 'Add a lot', 'Добавить лот', 'Ավելացնել լոտ')}</h4>
                <div className="admin-auction-form-row">
                    <CarPicker
                        cars={availableCars}
                        value={lotForm.car_id}
                        onChange={(id) => setLotForm((p) => ({ ...p, car_id: id }))}
                        placeholder={t('აირჩიეთ ავტომობილი', 'Choose a car', 'Выберите авто', 'Ընտրեք մեքենա')}
                    />
                    <input className="cx-input" type="number" step="0.01" required placeholder={t('საწყისი ფასი ($)', 'Start price ($)', 'Старт. цена ($)', 'Մեկնարկային գին ($)')} value={lotForm.start_price} onChange={(e) => setLotForm((p) => ({ ...p, start_price: e.target.value }))} />
                    <input className="cx-input" type="number" step="0.01" placeholder={t('reserve ($, დამალული)', 'Reserve ($, hidden)', 'Резерв ($, скрытый)', 'Ռեզերվ ($)')} value={lotForm.reserve_price} onChange={(e) => setLotForm((p) => ({ ...p, reserve_price: e.target.value }))} />
                    <input className="cx-input" type="number" step="0.01" placeholder={t('buy-now ($)', 'Buy-now ($)', 'Купить сразу ($)', 'Buy-now ($)')} value={lotForm.buy_now_price} onChange={(e) => setLotForm((p) => ({ ...p, buy_now_price: e.target.value }))} />
                    <select className="cx-select" value={lotForm.status} onChange={(e) => setLotForm((p) => ({ ...p, status: e.target.value }))}>
                        <option value="pending">pending</option>
                        <option value="open">open (pre-bid)</option>
                    </select>
                    <button className="cx-btn cx-btn-primary" disabled={busy}><PlusLg /> {t('დამატება', 'Add', 'Добавить', 'Ավելացնել')}</button>
                </div>
                {(() => {
                    const rate = (event && event.usd_rate) ? Number(event.usd_rate) : nbgRate;
                    if (!rate) return null;
                    const gel = (usd) => (usd !== '' && Number.isFinite(Number(usd)) ? formatPrice(Math.round(Number(usd) * rate * 100) / 100, 'GEL') : null);
                    const s = gel(lotForm.start_price); const r = gel(lotForm.reserve_price);
                    return (
                        <p className="auction-muted" style={{ margin: '10px 2px 0', fontSize: 13 }}>
                            {t('კურსი', 'Rate', 'Курс', 'Փոխարժեք')}: 1$ = ₾{rate}
                            {s ? ` · ${t('საწყისი', 'Start', 'Старт', 'Մեկն.')} ≈ ${s}` : ''}
                            {r ? ` · reserve ≈ ${r}` : ''}
                            {' — '}{t('ბიდები იდება ლარში', 'bids are placed in GEL', 'ставки в GEL', 'հայտերը GEL-ով')}
                        </p>
                    );
                })()}
            </form>

            {/* Below-reserve decisions */}
            {pending.length > 0 && (
                <div className="admin-console-section">
                    <h3>{t('reserve-ქვემოთ გადაწყვეტილებები', 'Below-reserve decisions', 'Решения ниже резерва', 'Ռեզերվից ցածր որոշումներ')}</h3>
                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead><tr>
                                <th>{t('მანქანა', 'Car', 'Авто', 'Մեքենա')}</th>
                                <th>{t('საუკეთესო ბიდი', 'Top bid', 'Лучшая ставка', 'Լավագույն հայտ')}</th>
                                <th>reserve</th>
                                <th>{t('გამარჯვებული', 'Winner', 'Победитель', 'Հաղթող')}</th>
                                <th className="admin-table-actions-col"></th>
                            </tr></thead>
                            <tbody>
                                {pending.map((o) => (
                                    <tr key={o.id}>
                                        <td>{carTitle(o)}</td>
                                        <td><strong>{formatPrice(o.winning_bid, 'GEL')}</strong></td>
                                        <td>{o.reserve_price != null ? formatPrice(o.reserve_price, 'GEL') : '—'}</td>
                                        <td>{[o.winner_name, o.winner_surname].filter(Boolean).join(' ') || `#${o.winner_id}`}</td>
                                        <td>
                                            <div className="admin-row-actions">
                                                <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={() => decide(o.lot_id, 'accept')}>{t('თანხმობა', 'Accept', 'Принять', 'Ընդունել')}</button>
                                                <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => decide(o.lot_id, 'counter')}>{t('კონტრ.', 'Counter', 'Встречно', 'Հակա')}</button>
                                                <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => decide(o.lot_id, 'reject')}>{t('უარი', 'Reject', 'Отказ', 'Մերժել')}</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Invoices */}
            {invoices.length > 0 && (
                <div className="admin-console-section">
                    <h3>{t('ინვოისები', 'Invoices', 'Счета', 'Հաշիվներ')}</h3>
                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead><tr>
                                <th>{t('მანქანა', 'Car', 'Авто', 'Մեքենա')}</th>
                                <th>{t('მყიდველი', 'Buyer', 'Покупатель', 'Գնորդ')}</th>
                                <th>{t('ჯამი', 'Total', 'Итого', 'Ընդ.')}</th>
                                <th>{t('სტატუსი', 'Status', 'Статус', 'Կարգ.')}</th>
                                <th className="admin-table-actions-col"></th>
                            </tr></thead>
                            <tbody>
                                {invoices.map((i) => (
                                    <tr key={i.id}>
                                        <td>{carTitle(i)}</td>
                                        <td>{[i.name, i.surname].filter(Boolean).join(' ') || `#${i.user_id}`}</td>
                                        <td><strong>{formatPrice(i.total, i.currency || 'GEL')}</strong></td>
                                        <td><span className={`cx-badge ${i.status === 'paid' ? 'cx-badge-sold' : i.status === 'cancelled' ? 'cx-badge-danger' : 'cx-badge-new'}`}>{i.status}</span></td>
                                        <td>
                                            {i.status === 'unpaid' && (
                                                <div className="admin-row-actions">
                                                    <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={() => invoiceAction(i.id, 'paid')}>{t('გადახდილი', 'Paid', 'Оплачен', 'Վճարված')}</button>
                                                    <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => invoiceAction(i.id, 'penalty')}>{t('ჯარიმა', 'Penalty', 'Штраф', 'Տուգանք')}</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminAuctionConsole;
